import type { JSONContent } from "@tiptap/react";

import {
  mentionKey,
  splitBodyOnMentions,
  type BodySegment,
  type MentionNames,
} from "@hightouch/lib/llm/asset-comments/mention-tokens";

import { MENTION_NODE_NAME } from "./mention-node";

/**
 * A stored comment body as the composer's document — the inverse of what the
 * composer submits.
 *
 * Mentions come back as nodes rather than as their `@[Name](user:4821)` tokens,
 * so they stay atomic while the text around them is edited and re-serialize
 * through the node's own `renderText`. Round-tripping the raw token as text
 * would leave the reader editing the id, where a typo silently retargets or
 * drops the mention.
 *
 * One paragraph per line, matching the `\n` block separator the composer submits
 * with, so a body's line breaks survive the round trip.
 */
export function bodyToEditorContent(
  body: string,
  mentionNames: MentionNames,
): JSONContent {
  const paragraphs: JSONContent[][] = [];
  let current: JSONContent[] = [];

  for (const segment of splitBodyOnMentions(body, mentionNames)) {
    if (segment.kind === "mention") {
      current.push(toMentionNode(segment));
      continue;
    }

    // Split inside the run rather than splitting the body up front: a token's
    // name run can itself contain a newline, and cutting the body first would
    // leave half a token to be read as prose.
    const lines = segment.text.split("\n");

    for (const [index, line] of lines.entries()) {
      if (index > 0) {
        paragraphs.push(current);
        current = [];
      }

      if (line.length > 0) {
        current.push({ type: "text", text: line });
      }
    }
  }

  paragraphs.push(current);

  return {
    type: "doc",
    // A paragraph with an empty `content` array is not a valid node; a blank
    // line is a paragraph with no content at all.
    content: paragraphs.map((content) =>
      content.length > 0
        ? { type: "paragraph", content }
        : { type: "paragraph" },
    ),
  };
}

function toMentionNode(
  segment: Extract<BodySegment, { kind: "mention" }>,
): JSONContent {
  const { mention, label } = segment;

  return {
    type: MENTION_NODE_NAME,
    attrs: {
      id:
        mention.kind === "user"
          ? String(mention.userId)
          : mention.kind === "reviewAgent"
            ? mention.reviewAgentId
            : mentionKey(mention),
      kind: mention.kind,
      label,
    },
  };
}
